import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef(({ className, children, hideClose, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
        className
      )}
      {...props}>
      {children}
      {/* hideClose: for a modal that must be explicitly acknowledged (see
          PaywallModal's mustAcknowledge) — omitting the X is necessary but
          not sufficient on its own; the caller must also keep the dialog's
          `open` prop controlled and not flip it false from onOpenChange for
          outside-click/Escape, or Radix's own handling still closes it. */}
      {!hideClose && (
        <DialogPrimitive.Close
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

// Shared "Apple-style" modal shell for the whole app (first built for the
// Add/Edit Transaction dialog): near-full-screen on mobile — matching how
// Apple's own big-form modals like Calendar's "New Event" behave — with a
// large centered sheet on desktop, and safe-area padding baked into the
// header/body below so nothing ever renders under a phone's notch or home
// indicator (index.html sets viewport-fit=cover, so without this the app
// draws straight under both). Pass `compact` for short, non-form content
// (a rating prompt, a paywall card) that shouldn't stretch to fill the
// mobile viewport — it still gets the same header-bar pattern and
// safe-area handling, just sized to its content instead of edge-to-edge.
// Phones: the on-screen keyboard overlays the page (iOS always, Android
// Chrome by default) without shrinking 100dvh, so a full-screen sheet's
// footer button ends up under the keyboard. Size the sheet to the visual
// viewport instead while it's open.
function useVisualViewportBox(enabled) {
  const [box, setBox] = React.useState(null)
  React.useEffect(() => {
    const vv = typeof window !== "undefined" ? window.visualViewport : null
    if (!enabled || !vv) return
    const update = () => {
      if (window.innerWidth >= 640) return setBox(null)
      setBox({ height: vv.height, top: vv.offsetTop })
    }
    update()
    vv.addEventListener("resize", update)
    vv.addEventListener("scroll", update)
    return () => {
      vv.removeEventListener("resize", update)
      vv.removeEventListener("scroll", update)
    }
  }, [enabled])
  return box
}

const DialogSheetContent = React.forwardRef(({ className, children, compact, style, ...props }, ref) => {
  const box = useVisualViewportBox(!compact)
  return (
  <DialogContent
    ref={ref}
    hideClose
    style={box ? { ...style, height: box.height, top: box.top } : style}
    className={cn(
      compact
        ? "bg-slate-900 border border-slate-700 p-0 gap-0 max-w-md w-[calc(100%-2rem)] rounded-2xl flex flex-col overflow-hidden max-h-[85vh]"
        : "bg-surface sm:bg-slate-900 border-0 sm:border sm:border-slate-700 p-0 gap-0 top-0 left-0 translate-x-0 translate-y-0 max-w-none w-full h-full h-[100dvh] rounded-none sm:top-[50%] sm:left-[50%] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:max-w-xl sm:w-full sm:h-auto sm:max-h-[88vh] sm:rounded-2xl flex flex-col overflow-hidden",
      className
    )}
    {...props}>
    {children}
  </DialogContent>
  )
})
DialogSheetContent.displayName = "DialogSheetContent"

// Cancelar / title / primary-action bar, always visible and never part of
// the scrollable body — the whole point being fixed: a long dropdown or
// grid can never grow to cover the primary action the way a bottom button
// used to. `action` is a fully custom node (styling/disabled/busy state
// varies per modal); omit it for a header with no primary action.
const DialogSheetHeader = ({ title, onCancel, cancelLabel = "Cancelar", cancelTestId, action, className }) => (
  <div className={cn("shrink-0 pt-[env(safe-area-inset-top)]", className)}>
    <div className="flex items-center justify-between px-4 h-14 border-b border-white/5">
      {onCancel ? (
        <button
          type="button"
          data-testid={cancelTestId}
          onClick={onCancel}
          className="text-slate-400 hover:text-white text-[15px] font-medium px-2 py-2 -ml-2 rounded-lg transition-colors active:scale-[0.97]"
        >
          {cancelLabel}
        </button>
      ) : <span className="w-10" />}
      <DialogTitle className="text-white text-[15px] font-semibold truncate px-2">{title}</DialogTitle>
      {action || <span className="w-10" />}
    </div>
  </div>
)
DialogSheetHeader.displayName = "DialogSheetHeader"

// The only part that scrolls — bottom safe-area padding keeps the last
// field clear of the home indicator on notched phones.
const DialogSheetBody = ({ className, children, ...props }) => (
  <div className={cn("flex-1 overflow-y-auto px-5 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] space-y-8", className)} {...props}>
    {children}
  </div>
)
DialogSheetBody.displayName = "DialogSheetBody"

// Optional fixed bar under the body for a big primary button in the thumb
// zone (redesign.md, Fase 6). Never scrolls; clears the home indicator.
const DialogSheetFooter = ({ className, children, ...props }) => (
  <div className={cn("shrink-0 border-t border-white/5 px-5 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]", className)} {...props}>
    {children}
  </div>
)
DialogSheetFooter.displayName = "DialogSheetFooter"

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogSheetContent,
  DialogSheetHeader,
  DialogSheetBody,
  DialogSheetFooter,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
